<template>
  <b-row>
    <b-col cols="3">
      <b-card id="filters-form-card" header="Filtri" header-tag="header">
        <template #header>
          <div class="custom-card-header">
            <strong>
              <b-icon-funnel/>&nbsp;Filtri</strong>
          </div>
        </template>
        <b-card-body style="padding: 0.5rem">
          <b-form id="form" @submit="findCustomers">
            <b-form-group class="mb-3">
              <b-form-checkbox
                id="include-inactive-checkbox"
                v-model="selectedFilters.includeInactive"
                :value="true"
                :unchecked-value="false"
                switch
              >
                Includi tessere non attive
              </b-form-checkbox>
            </b-form-group>
            <b-form-group label="Codice tessera">
              <b-form-input
                name="Codice Tessera"
                id="barcode"
                v-model="selectedFilters.barcode"
                :disabled="searchByPersonalData"
                v-validate="{ min: 13, max: 13, regex: /^0229|0206/}"
                :state="stateValidation('Codice Tessera')"
                aria-describedby="input-barcode-feedback"
              />
              <b-form-invalid-feedback id="input-barcode-feedback">
                {{ veeErrors.first('Codice Tessera')}}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group label="Filiale">
              <ShopSelect ref="shop" :required="false" v-model="selectedFilters.shop"/>
            </b-form-group>
            <b-form-group label="Cognome">
              <b-form-input id="surname" v-model="selectedFilters.lastName" :disabled="searchByBarcode"/>
            </b-form-group>
            <b-form-group label="Nome">
              <b-form-input id="name" v-model="selectedFilters.firstName" :disabled="searchByBarcode"/>
            </b-form-group>
            <b-form-group label="Data di nascita">
              <DatePicker ref="birthDate" v-model="selectedFilters.birthDate" id="birth-date" description="Data nascita" :disabled="searchByBarcode"/>
            </b-form-group>
            <b-form-group label="Convenzione">
              <b-form-select v-model="selectedFilters.affiliation" class="mb-3">
                <template #first>
                  <b-form-select-option :value="null" disabled>-- Selezionare una delle possibilità --</b-form-select-option>
                </template>
                <b-form-select-option value="UniTn">UniTn</b-form-select-option>
              </b-form-select>
            </b-form-group>
            <b-button type="submit" :disabled="showSpinner" variant="primary" block id="search">
              <b-icon-search/>&nbsp;Cerca
            </b-button>
            <b-button v-show="showDuplicateFeature" :disabled="showSpinner" @click="showCustomersDuplicateFirstPage" variant="warning" block>
              <b-spinner v-show="showSpinner" small type="grow"></b-spinner>
              <b-icon-search/>&nbsp;Trova Duplicati
            </b-button>
            <b-button @click="exportCsv" :disabled="isDownloading" variant="success" block >
              <b-spinner small v-show="isDownloading"></b-spinner>
              <b-icon-arrow-bar-down/>&nbsp;Download csv
            </b-button>
          </b-form>
        </b-card-body>
      </b-card>
    </b-col>
    <b-col cols="9">
      <b-card id="customers-table-card" header-bg-variant="light">
        <template #header>
          <div style="height: 1.5rem">
            <span v-show="filteredCustomers.length > 0"><strong><b-icon-people-fill/>&nbsp;Anagrafiche trovate</strong></span>
            <div v-show="searched" class="float-right">
              <h6 class="filters-list-head">
                Filtri applicati:
                <b-badge class="filter-badge" v-show="confirmedFilters.barcode" @click="removeFilter('barcode')" variant="info" pill tag="button">Codice Tessera: <i>{{
                    confirmedFilters.barcode
                  }}</i>&nbsp;<b-icon-x-circle-fill
                  variant="light"/>
                </b-badge>
                <b-badge class="filter-badge" v-if="confirmedFilters.shop" @click="removeFilter('shop')" variant="info" pill tag="button">Filiale: <i>{{
                    confirmedFilters.shop.code
                  }}</i>&nbsp;<b-icon-x-circle-fill
                  variant="light"/>
                </b-badge>
                <b-badge class="filter-badge" v-show="confirmedFilters.lastName" @click="removeFilter('lastName')" variant="info" pill tag="button">Cognome: <i>{{ confirmedFilters.lastName }}</i>&nbsp;<b-icon-x-circle-fill
                  variant="light"/>
                </b-badge>
                <b-badge class="filter-badge" v-show="confirmedFilters.firstName" @click="removeFilter('firstName')" variant="info" pill tag="button">Nome: <i>{{ confirmedFilters.firstName }}</i>&nbsp;<b-icon-x-circle-fill
                  variant="light"/>
                </b-badge>
                <b-badge class="filter-badge" v-show="confirmedFilters.affiliation" @click="removeFilter('affiliation')" variant="info" pill tag="button">Nome: <i>{{ confirmedFilters.affiliation }}</i>&nbsp;<b-icon-x-circle-fill
                  variant="light"/>
                </b-badge>
                <b-badge class="filter-badge" v-show="confirmedFilters.birthDate" @click="removeFilter('birthDate')" variant="info" pill tag="button">Data di nascita:
                  <i>{{ asItalianFormat(confirmedFilters.birthDate) }}</i>&nbsp;<b-icon-x-circle-fill variant="light"/>
                </b-badge>
              </h6>
            </div>
          </div>
        </template>
        <b-card-body v-show="filteredCustomers.length > 0">
          <b-table sticky-header="72vh" :no-border-collapse="true" id="customers-table" fixed responsive hover :fields="tableColumns" :items="filteredCustomers">
            <template #cell(shop)="data">
              <span class="cap-smalled">{{ formatShop(data.item.shopCode) }}</span>
            </template>
            <template #cell(birthDate)="data">
              {{ asItalianFormat(data.item.birthDate) }}
            </template>
            <template #cell(actions)="data" style="text-align: right">
              <b-button @click="selectPreview(data.item)" pill title="Visualizza dettaglio storico" size="sm" variant="info" ref="history" v-b-tooltip.hover>
                <b-icon-book-half size="sm"/>&nbsp;Dettaglio
              </b-button>
            </template>
            <template #cell(affiliation)="data">
              <b-badge v-if="affiliationStillValid(data.item.affiliation)" pill variant="info">{{ data.item.affiliation.name }}</b-badge>
            </template>
            <template #cell(status)="data">
              <b-badge v-if="data.item.hasActiveCard" variant="success">Attiva</b-badge>
              <b-badge v-else variant="secondary">Nessuna tessera attiva</b-badge>
            </template>
          </b-table>
        </b-card-body>
        <b-card-body v-show="filteredCustomers.length === 0 && showSpinner === false" class="text-center">
          <h1>
            <b-icon-search/>
          </h1>
          <h3>Nessuna anagrafica trovata</h3>
          <p>Compila il modulo dei filtri soprastante per iniziare una nuova ricerca delle anagrafiche.</p>
        </b-card-body>
      </b-card>
      <b-pagination
        align="left"
        v-model="currentPage"
        :per-page="pageSize"
        :total-rows="totalRows"
        @change="showNextPage"
      ></b-pagination>
    </b-col>
  </b-row>
</template>

<script>
import DatePicker from '../DatePicker'
import moment from 'moment'
import eventBus from '@/EventBus'
import ShopsApi from "@/api/ShopsApi"
import CustomersApi from "../../api/CustomersApi";
import ShopSelect from "@/components/ShopSelect.vue";
import MembershipApi from "@/api/MembershipApi";

const isNothing = field => field === undefined || field === null || field === ''

export default {
  name: 'Search',
  props: ['session'],
  provide() {
    return {parentValidator: this.$validator}
  },
  components: {ShopSelect, DatePicker},
  data: () => ({
    customers: [],
    searched: false,
    selectedFilters: {},
    confirmedFilters: {},
    showSpinner: false,
    shops: null,
    currentPage: 1,
    totalRows: null,
    pageSize: null,
    searchedDuplicates: false,
    isDownloadingStats: false
  }),
  computed: {
    filteredCustomers() {
      if (!this.selectedFilters.includeInactive) {
        return this.customers.filter(customer => customer.hasActiveCard);
      }
      return this.customers;
    },
    searchByBarcode() { return !!this.selectedFilters.barcode },
    searchByPersonalData() { return !!(this.selectedFilters.lastName || this.selectedFilters.firstName || this.selectedFilters.birthDate) },
    showDuplicateFeature () { return process.env.VUE_APP_FEATURE_SHOW_DUPLICATES === 'true' },
    tableColumns() {
      const columns = [
        {key: 'shop', label: 'Negozio', sortable: true},
        {key: 'barcode', label: 'Codice Tessera', sortable: true, class: 'text-left'},
        {key: 'lastName', label: 'Cognome', sortable: true},
        {key: 'firstName', label: 'Nome', sortable: true},
        {key: 'birthDate', label: 'Data di nascita', sortable: true},
        {key: 'affiliation', label: 'Cluster', sortable: true}
      ];

      if (this.selectedFilters.includeInactive) {
        columns.splice(5, 0, { key: 'status', label: 'Stato', sortable: true });
      }

      columns.push({ key: 'actions', label: '', class: 'text-right' });
      return columns;
    },
  },
  mounted() {
    ShopsApi.all()
      .then(shops => this.shops = new Map(shops.map(shop => [shop.code, shop.name])))
  },
  methods: {
    stateValidation(ref) {
      if (this.veeFields[ref] && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
        return !this.veeErrors.has(ref)
      } else {
        return null
      }
    },
    selectPreview(customerPreview) {
      eventBus.$emit('customer-selected', customerPreview)
    },
    defineFilters(page) {
      this.confirmedFilters = {...this.selectedFilters}

      Object.keys(this.selectedFilters)
        .filter(property => isNothing(this.selectedFilters[property]))
        .forEach(property => delete this.selectedFilters[property])

      return  {
        barcode: this.selectedFilters.barcode,
        shopId: this.selectedFilters.shop ? this.selectedFilters.shop.code : null,
        lastName: this.selectedFilters.lastName,
        firstName: this.selectedFilters.firstName,
        birthDate: this.selectedFilters.birthDate,
        affiliation: this.selectedFilters.affiliation,
        includeInactive: this.selectedFilters.includeInactive || false,
        page: page
      }
    },
    findCustomerByFilters(page) {
      if (this.selectedFilters.barcode) {
        this.selectedFilters.barcode = this.selectedFilters.barcode.replace(/\s/g, '');
      }
      this.$validator.validateAll().then(result => {

        if (result) {
          this.searchedDuplicates = false
          eventBus.$emit('customers-searched')
          let params = this.defineFilters(page)

          CustomersApi.findCustomerPreviewBy(params).then(result => {
            this.customers = result.data.previews
            this.searched = !this.isEmpty(this.selectedFilters)

            this.pageSize = result.data.pageSize
            this.totalRows = result.data.pageCount*this.pageSize
          })
        }
      })
    },
    findCustomers(event) {
      event && event.preventDefault()
      this.currentPage = 1
      this.findCustomerByFilters(1)
    },
    showCustomersDuplicateFirstPage() {
      this.currentPage = 1
      this.searchedDuplicates = true
      this.findDuplicateCustomers(1)
    },
    showNextPage(page) {
      if (this.searchedDuplicates) this.findDuplicateCustomers(page)
      else this.findCustomerByFilters(page)
    },
    findDuplicateCustomers(page) {
      this.showSpinner = true
      eventBus.$emit('customers-searched')

      CustomersApi.findDuplicate({ page: page })
        .then(result => {
          this.customers = result.data.previews
          this.searched = !this.isEmpty(this.selectedFilters)

          this.pageSize = result.data.pageSize
          this.totalRows = result.data.pageCount*this.pageSize
          this.showSpinner = false
        })
    },
    removeFilter(field) {
      delete this.selectedFilters[field]
      if (field === 'birthDate') {
        this.$refs.birthDate.reset()
      }
      if (field === 'shop') {
        this.$refs.shop.reset()
      }
      if (this.isEmpty(this.selectedFilters)) {
        this.searched = false
      }
      this.findCustomerByFilters()
    },
    isEmpty(object) { return Object.keys(object).length === 0 },
    asItalianFormat: date => date ? moment(date).format('DD/MM/YYYY') : "",
    formatShop(id) { return `${id} - ${this.shops.get(id)}` },
    affiliationStillValid(affiliation) {
      if(affiliation == null || affiliation.endAt == null) return false
      return moment.utc().isBefore(moment.utc(affiliation.endAt))
    },
    exportCsv() {
      this.isDownloading = true
      let params = this.defineFilters()

      CustomersApi
        .downloadCsv(params)
        .then(result => {
          console.log(result)
          CustomersApi.fileDownload(result)
        })
        .finally(() => {
          this.isDownloading = false
        })
    }
  }
}
</script>

<style>

button.filter-badge {
  border: unset;
  margin-right: 0.5rem;
}

#filters-form-card.card > .card-body {
  padding: unset;
}

#customers-table.table th, #customers-table.table td {
  vertical-align: middle;
}

#customers-table-card.card > .card-body, #customers-table-card.card > .card-body > .card-body > .table-responsive {
  margin-bottom: unset;
}

#barcodes-table > thead, #consensus-table > thead {
  display: none;
}

div#customers-container > div.row {
  margin-bottom: 1rem
}

.small-column {
  width: 3rem;
}
</style>
